(function () {
  const wishlistedProductIds = new Set();

  const optimisticAdds = new Set();
  const optimisticRemoves = new Set();

  function markWishlisted(productId, isWishlisted) {
    if (productId == null) return;

    const id = String(productId);

    if (isWishlisted) {
      wishlistedProductIds.add(id);
      optimisticAdds.add(id);
      optimisticRemoves.delete(id);
    } else {
      wishlistedProductIds.delete(id);
      optimisticRemoves.add(id);
      optimisticAdds.delete(id);
    }

    updateAllCardButtons();
  }

  function reconcileOptimisticState(serverIds) {
    optimisticAdds.forEach((id) => {
      if (serverIds.has(id)) {
        optimisticAdds.delete(id);
        return;
      }
      wishlistedProductIds.add(id);
    });

    optimisticRemoves.forEach((id) => {
      if (!serverIds.has(id)) {
        optimisticRemoves.delete(id);
        return;
      }
      wishlistedProductIds.delete(id);
    });
  }

  function getStrings() {
    return window.variantStrings || {};
  }

  function getFullProductUrl(path) {
    if (!path) return window.location.href;
    const clean = String(path).split("?")[0];
    if (/^https?:\/\//i.test(clean)) return clean;
    return `${window.location.origin}${clean.startsWith("/") ? clean : `/${clean}`}`;
  }

  function isThemeWishlistButton(element) {
    return element?.matches?.('quick-add[data-mode="wishlist"]');
  }

  const SWYM_NODE_SELECTOR = [
    '[class*="swym" i]',
    '[id*="swym" i]',
    "[data-swaction]",
    "[data-swym-id]",
  ].join(",");

  function isSwymNode(element) {
    if (!element || element.nodeType !== 1) return false;
    if (element.tagName.toLowerCase().startsWith("swym-")) return true;
    return element.matches?.(SWYM_NODE_SELECTOR) === true;
  }

  function purgeSwymDuplicateButtons(root = document) {
    const cards =
      root.nodeType === 1 && root.matches?.("product-card, .card-product")
        ? [root]
        : Array.from(root.querySelectorAll("product-card, .card-product"));

    cards.forEach((card) => {
      card.querySelectorAll("*").forEach((element) => {
        if (!isSwymNode(element)) return;
        if (element.closest('quick-add[data-mode="wishlist"]')) return;
        element.remove();
      });
    });
  }

  function updateCardButton(button) {
    if (!button?.dataset?.productId) return;

    const isWishlisted = wishlistedProductIds.has(String(button.dataset.productId));
    const strings = getStrings();

    button.classList.toggle("is-wishlisted", isWishlisted);
    button.setAttribute("aria-pressed", isWishlisted ? "true" : "false");
    button.setAttribute(
      "aria-label",
      isWishlisted
        ? strings.removeFromWishlist || "Remove from wishlist"
        : strings.addToWishlist || "Add to wishlist",
    );
  }

  function updateAllCardButtons() {
    document
      .querySelectorAll('quick-add[data-mode="wishlist"]')
      .forEach(updateCardButton);
  }

  function loadWishlistState(swat) {
    if (!swat || typeof swat.fetch !== "function") return;

    swat.fetch(
      (items) => {
        const serverIds = new Set();

        (items || []).forEach((item) => {
          if (item?.empi != null) {
            serverIds.add(String(item.empi));
          }
        });

        wishlistedProductIds.clear();
        serverIds.forEach((id) => wishlistedProductIds.add(id));
        reconcileOptimisticState(serverIds);

        purgeSwymDuplicateButtons();
        updateAllCardButtons();
      },
      () => {},
    );
  }

  function prefetchWishlistCache(swat) {
    if (!swat || typeof swat.fetchLists !== "function") return;

    swat.fetchLists({
      callbackFn: () => {},
      errorFn: () => {},
    });
  }

  function removeFromWishlist(trigger, swat) {
    const productId = trigger.dataset.productId;
    const fallbackUrl = getFullProductUrl(trigger.dataset.productUrl);

    swat.fetch(
      (items) => {
        const matches = (items || []).filter(
          (item) => String(item.empi) === String(productId),
        );

        if (!matches.length) {
          markWishlisted(productId, false);
          return;
        }

        let completed = 0;

        const finish = () => {
          completed += 1;
          if (completed < matches.length) return;

          markWishlisted(productId, false);
          loadWishlistState(swat);

          swat.evtLayer?.dispatchEvent(
            new CustomEvent(swat.JSEvents?.removedFromWishlist || "sw:removedfromwishlist", {
              detail: { d: matches[0] },
            }),
          );

          if (typeof swat.updateWishlistCount === "function") {
            swat.updateWishlistCount();
          }

          swat.ui?.showSuccessNotification?.({
            message:
              getStrings().removedFromWishlist || "Removed from wishlist",
          });
        };

        matches.forEach((item) => {
          const product = {
            epi: item.epi,
            empi: item.empi,
            du: item.du || fallbackUrl,
          };

          if (typeof swat.removeFromWishList === "function") {
            swat.removeFromWishList(product, finish, finish);
            return;
          }

          swat.fetchLists({
            callbackFn: (lists) => {
              const listId = lists?.[0]?.lid;
              if (!listId) {
                finish();
                return;
              }
              swat.deleteFromList(listId, product, finish, finish);
            },
            errorFn: finish,
          });
        });
      },
      () => {},
    );
  }

  function observeProductCards() {
    let scheduled = false;

    const observer = new MutationObserver((mutations) => {
      if (scheduled) return;

      const addedSwymNode = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            node.closest("product-card, .card-product") &&
            (isSwymNode(node) || node.querySelector(SWYM_NODE_SELECTOR)),
        ),
      );

      const addedCard = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.("product-card, .card-product") ||
              node.querySelector?.("product-card, .card-product")),
        ),
      );

      if (!addedSwymNode && !addedCard) return;

      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        purgeSwymDuplicateButtons();
        updateAllCardButtons();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    purgeSwymDuplicateButtons();
    observeProductCards();
    updateAllCardButtons();
  }

  document.addEventListener(
    "click",
    (event) => {
      const trigger = event.target.closest('quick-add[data-mode="wishlist"]');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (trigger.classList.contains("is-wishlisted")) {
        if (window._swat) {
          removeFromWishlist(trigger, window._swat);
        } else {
          window.SwymCallbacks = window.SwymCallbacks || [];
          window.SwymCallbacks.push((swat) => removeFromWishlist(trigger, swat));
        }
        return;
      }

      if (typeof trigger.open === "function") {
        trigger.open(event);
      }
    },
    true,
  );

  document.addEventListener("product-card:replaced", (event) => {
    const card = event.detail?.card;
    purgeSwymDuplicateButtons(card || document);
    updateAllCardButtons();
  });

  document.addEventListener("wishlist:state-changed", (event) => {
    const detail = event.detail;

    if (detail?.productId != null) {
      markWishlisted(detail.productId, detail.wishlisted !== false);
    }

    if (window._swat) {
      loadWishlistState(window._swat);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push((swat) => {
    purgeSwymDuplicateButtons();
    prefetchWishlistCache(swat);
    loadWishlistState(swat);

    swat.evtLayer?.addEventListener(
      swat.JSEvents?.addedToWishlist || "sw:addedtowishlist",
      () => loadWishlistState(swat),
    );
    swat.evtLayer?.addEventListener(
      swat.JSEvents?.removedFromWishlist || "sw:removedfromwishlist",
      () => loadWishlistState(swat),
    );
  });

  setTimeout(() => purgeSwymDuplicateButtons(), 1000);
  setTimeout(() => purgeSwymDuplicateButtons(), 3000);
})();
